import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, ShieldCheck, TrendingUp, Mail, Eye, EyeOff, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/login/email`, {
        email: email.trim(),
        password
      }, { withCredentials: true });
      
      toast.success("Logged in successfully!");
      navigate("/dashboard", { replace: true });
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1749310726959-d8fccfef7ee4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MDcyOTg4N3ww&ixlib=rb-4.1.0&q=85"
          alt="Modern office architecture"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <blockquote className="text-2xl font-heading leading-relaxed mb-6 animate-fade-in">
            "Streamline your tax practice with intelligent document analysis and professional client reporting."
          </blockquote>
          <div className="flex items-center gap-8 text-slate-300 animate-fade-in-delay-1">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span className="font-body text-sm">Smart Analysis</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-body text-sm">Secure & Private</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              <span className="font-body text-sm">Prior Year Comparison</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 bg-slate-50 relative grain-overlay">
        <div className="max-w-md w-full mx-auto">
          {/* Logo/Brand */}
          <div className="mb-12 animate-fade-in">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 tracking-tight">
                Refund Scope
              </h1>
            </div>
            <p className="text-slate-500 font-body text-sm mt-1">
              Tax Return Analysis Platform
            </p>
          </div>

          {/* Welcome Message */}
          <div className="mb-8 animate-fade-in-delay-1">
            <h2 className="text-4xl font-heading font-bold text-slate-900 tracking-tight mb-3">
              Welcome back
            </h2>
            <p className="text-slate-600 font-body">
              Sign in to access your tax practice dashboard.
            </p>
          </div>

          {/* Login Options */}
          <div className="animate-fade-in-delay-2">
            <Tabs defaultValue="email" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="google">Google</TabsTrigger>
              </TabsList>
              
              <TabsContent value="email">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 text-slate-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>
                <p className="text-xs text-center text-slate-500 mt-4">
                  Need an account? Ask your admin for an invite link.
                </p>
              </TabsContent>
              
              <TabsContent value="google">
                <Button
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                  className="w-full h-14 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 font-body font-medium rounded-md shadow-sm flex items-center justify-center gap-3 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>
                <p className="text-xs text-center text-slate-500 mt-4">
                  Sign in with your Google account
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* Features */}
          <div className="mt-10 pt-8 border-t border-slate-200 animate-fade-in-delay-3">
            <p className="text-xs text-slate-500 font-body mb-4 uppercase tracking-wider">
              What you can do
            </p>
            <ul className="space-y-3 text-sm text-slate-600 font-body">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2"></div>
                <span>Upload client tax returns (PDF, JPG, PNG)</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2"></div>
                <span>AI-powered analysis of Federal, State, Local & School taxes</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2"></div>
                <span>Generate professional PDF reports for clients</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
